import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { Calculator, Zap } from 'lucide-react';
import { listMaterials, calcFeedSpeed } from '@/lib/api';
import { toast } from 'sonner';
import { IDS } from '@/constants/testIds/wizard';

export const FeedSpeedDialog = ({ open, onOpenChange, onApply }) => {
  const [materials, setMaterials] = useState({});
  const [material, setMaterial] = useState('aluminum');
  const [toolDia, setToolDia] = useState(6);
  const [flutes, setFlutes] = useState(2);
  const [toolMat, setToolMat] = useState('carbide');
  const [operation, setOperation] = useState('profile');
  const [result, setResult] = useState(null);

  useEffect(() => { if (open) listMaterials().then(d => setMaterials(d.data || {})).catch(() => {}); }, [open]);

  const compute = async () => {
    try {
      const r = await calcFeedSpeed({ material, tool_diameter: toolDia, flutes, tool_material: toolMat, operation });
      setResult(r);
    } catch { toast.error('Szamitasi hiba'); }
  };

  useEffect(() => { if (open) compute(); }, [open, material, toolDia, flutes, toolMat, operation]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-card border-border">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Calculator className="h-4 w-4 text-primary" /> Feed / Speed kalkulator</DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">Anyag alapu vagasi parameter kalkulator.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Anyag</Label>
            <Select value={material} onValueChange={setMaterial}>
              <SelectTrigger data-testid={IDS.fs.material}><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(materials).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.name_hu || k}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Muvelet</Label>
            <Select value={operation} onValueChange={setOperation}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="profile">Kontur/Profile</SelectItem>
                <SelectItem value="slot">Horony</SelectItem>
                <SelectItem value="pocket">Zsebmaras</SelectItem>
                <SelectItem value="drilling">Furas</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Szerszam anyag</Label>
            <Select value={toolMat} onValueChange={setToolMat}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="hss">HSS</SelectItem>
                <SelectItem value="cobalt">Kobalt</SelectItem>
                <SelectItem value="carbide">Kemenyfem</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Szerszam atmero (mm)</Label><Input type="number" step="0.1" value={toolDia} onChange={e => setToolDia(parseFloat(e.target.value))} /></div>
          <div><Label className="text-xs">Elek szama</Label><Input type="number" value={flutes} onChange={e => setFlutes(parseInt(e.target.value || '1', 10))} /></div>
        </div>
        <div className="grid grid-cols-3 gap-2 mt-4" data-testid={IDS.fs.result}>
          <Card className="p-3 bg-black/30 border-border/60">
            <div className="text-[10px] uppercase text-muted-foreground">Fordulatszam</div>
            <div className="text-2xl font-semibold tabular text-primary">{result?.rpm ?? '-'} <span className="text-xs text-muted-foreground">RPM</span></div>
          </Card>
          <Card className="p-3 bg-black/30 border-border/60">
            <div className="text-[10px] uppercase text-muted-foreground">Feed XY</div>
            <div className="text-2xl font-semibold tabular text-primary">{result?.feed_xy_mm_min ?? '-'} <span className="text-xs text-muted-foreground">mm/min</span></div>
          </Card>
          <Card className="p-3 bg-black/30 border-border/60">
            <div className="text-[10px] uppercase text-muted-foreground">Feed Z (beszuras)</div>
            <div className="text-2xl font-semibold tabular text-primary">{result?.feed_z_mm_min ?? '-'} <span className="text-xs text-muted-foreground">mm/min</span></div>
          </Card>
          <Card className="p-3 bg-black/30 border-border/60 col-span-3 flex justify-between items-center">
            <div className="text-xs">SFM: <span className="tabular text-foreground">{result?.sfm ?? '-'}</span> • Chip load: <span className="tabular text-foreground">{result?.chip_load_mm ?? '-'} mm</span></div>
            <Button onClick={() => result && onApply(result)} disabled={!result} data-testid={IDS.fs.apply}><Zap className="h-4 w-4 mr-1" /> Alkalmazasa</Button>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FeedSpeedDialog;
